const STATE_SUFFIX = /,?\s+(NJ|NY|CT|PA|CA|TX|FL|IL|WA|MA|CO|GA|AZ|NC|VA|OH|MI|MN|WI|MO|TN|IN|MD|OR|SC|AL|LA|KY|OK|IA|MS|AR|UT|NV|NM|ID|NE|WV|HI|NH|ME|MT|RI|DE|SD|ND|AK|VT|WY|DC|New Jersey|New York|Connecticut|Pennsylvania)\s*$/i;
const INLINE_STATE_TERMS = /\b(?:NJ|NY|CT|PA|New Jersey|New York|Connecticut|Pennsylvania)\b/gi;
const GENERIC_REAL_ESTATE_TERMS = /\b(?:homes?|houses?|real estate|listings?|for sale|properties|property)\b/gi;

const TOWN_ALIASES: Record<string, string> = {
  "morris twp": "Morris Township",
  "morris twp.": "Morris Township",
  "morris township": "Morris Township",
  nyc: "New York",
  manhattan: "New York",
  "new york city": "New York",
};

const KNOWN_TOWNS = [
  "Morristown", "Morris Township", "Morris Plains", "Madison", "Chatham", "Mendham",
  "Mendham Township", "Florham Park", "Parsippany", "Whippany", "East Hanover",
  "Chester", "Randolph", "Bernardsville", "Basking Ridge", "Summit", "Short Hills",
  "Millburn", "Maplewood", "Montclair", "West Orange", "Livingston", "Westfield",
  "Hoboken", "Jersey City", "Weehawken", "Edgewater", "Union City", "Fort Lee",
  "Secaucus", "Bayonne", "North Bergen", "West New York", "Kearny", "Harrison",
  "Ridgewood", "Glen Rock", "Wyckoff", "Ho-Ho-Kus", "Paramus", "Tenafly",
  "Sparta", "Newton", "Sussex", "Vernon", "Hopatcong", "Hackettstown", "Warren", "Watchung",
  "Princeton", "New York", "Brooklyn", "Queens", "Bronx", "Staten Island",
  "White Plains", "Yonkers", "Scarsdale", "Bronxville", "New Rochelle", "Rye",
  "Garden City", "Great Neck", "Huntington", "Hartford", "Stamford", "Greenwich", "Darien",
  "New Canaan", "Norwalk", "Westport", "Fairfield", "Ridgefield", "Danbury",
];

function normalizeKey(value: string) {
  return value
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const KNOWN_TOWN_STATES: Record<string, string> = KNOWN_TOWNS.reduce<Record<string, string>>((acc, town) => {
  acc[normalizeKey(town)] = "NJ";
  return acc;
}, {});

for (const town of [
  "New York", "Brooklyn", "Queens", "Bronx", "Staten Island", "White Plains",
  "Yonkers", "Scarsdale", "Bronxville", "New Rochelle", "Rye", "Garden City",
  "Great Neck", "Huntington",
]) {
  KNOWN_TOWN_STATES[normalizeKey(town)] = "NY";
}

for (const town of ["Hartford", "Stamford", "Greenwich", "Darien", "New Canaan", "Norwalk", "Westport", "Fairfield", "Ridgefield", "Danbury"]) {
  KNOWN_TOWN_STATES[normalizeKey(town)] = "CT";
}

const AMBIGUOUS_TOWN_KEYS = new Set(["fairfield", "harrison", "ridgefield"]);

function explicitStateFromValue(value: string): string | null {
  if (/\b(?:NJ|New Jersey)\b/i.test(value)) return "NJ";
  if (/\b(?:NY|New York)\b/i.test(value)) return "NY";
  if (/\b(?:CT|Connecticut)\b/i.test(value)) return "CT";
  if (/\b(?:PA|Pennsylvania)\b/i.test(value)) return "PA";
  return null;
}

function titleCase(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.split("-").map(piece => piece ? piece[0].toUpperCase() + piece.slice(1).toLowerCase() : piece).join("-"))
    .join(" ");
}

export function cleanLocationPhrase(value: string) {
  let cleaned = value
    .replace(/\b(?:homes?|houses?|properties|property|listings?)\s+in\s+/i, "")
    .replace(/\bin\s+$/i, "")
    .replace(STATE_SUFFIX, "")
    .replace(INLINE_STATE_TERMS, "")
    .replace(GENERIC_REAL_ESTATE_TERMS, " ")
    .replace(/\s+/g, " ")
    .trim();

  cleaned = cleaned.replace(/,\s*$/, "").trim();
  const key = normalizeKey(cleaned);
  const alias = TOWN_ALIASES[key];
  if (alias) return alias;
  const known = KNOWN_TOWNS.find(town => normalizeKey(town) === key);
  return known || titleCase(cleaned);
}

export function normalizeLocationQuery(value: string): string | null {
  const cleaned = cleanLocationPhrase(value);
  if (!cleaned || cleaned.length < 2) return null;
  return cleaned;
}

export function normalizeSearchQueryForSubmission(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const withoutState = trimmed.replace(STATE_SUFFIX, "").trim();
  const genericOnly = new RegExp(
    `^(?:homes?|houses?|properties|property|listings?)\\s+in\\s+.+|.+\\s+(?:homes?|houses?|real estate|listings?|for sale|properties|property)$`,
    "i",
  );
  if (genericOnly.test(withoutState) && !/\d|\$|under|over|max|min|bed|bath|yard|privacy|train|school|corner|busy|garage|pool|renovat|updated/i.test(withoutState)) {
    const explicitState = explicitStateFromValue(trimmed);
    const known = knownTownFromQuery(withoutState);
    if (known) {
      const key = normalizeKey(known);
      const inferredState = AMBIGUOUS_TOWN_KEYS.has(key) ? null : KNOWN_TOWN_STATES[key] || "NJ";
      const state = explicitState || inferredState;
      return state ? `${known}, ${state}` : known;
    }
    const location = normalizeLocationQuery(withoutState);
    if (location && explicitState) return `${location}, ${explicitState}`;
    return location || trimmed;
  }
  return trimmed;
}

export function knownTownFromQuery(value: string): string | null {
  const cleaned = cleanLocationPhrase(value);
  const key = normalizeKey(cleaned);
  return KNOWN_TOWNS.find(town => normalizeKey(town) === key) || TOWN_ALIASES[key] || null;
}

export function stateFromLocationQuery(value: string): string | null {
  const explicitState = explicitStateFromValue(value);
  if (explicitState) return explicitState;

  const cleaned = cleanLocationPhrase(value);
  const key = normalizeKey(cleaned);
  if (!key || AMBIGUOUS_TOWN_KEYS.has(key)) return null;
  return KNOWN_TOWN_STATES[key] || null;
}
